import { useEffect, useState, useCallback, useRef } from 'react';
import { AscDesc } from 'stream-chat';

import { useChatClient } from '@bizzabo/chat-core';
import { instanaCustomEvent } from '@bizzabo/instana-api';

import ContactList from '../ContactList';
import { useErrorHandler } from 'react-error-boundary';

type SearchOptions = {
    limit?: number;
    offset?: number;
};

type Props = {
    eventId: number;
    searchTerm: string;
    onContactItemClick: (attendeeId: number) => void;
};

const LIMIT = 30;

const ContactListWrapper: React.FC<Props> = ({ eventId, searchTerm, onContactItemClick }) => {
    const { client } = useChatClient();
    const [users, setUsers] = useState([]);
    const handleError = useErrorHandler();
    const offset = useRef(0);

    const fetchUsers = useCallback(
        async (search?: string, options?: SearchOptions) => {
            const sort = { firstName: 1 as AscDesc };
            const baseQuery = {
                isHidden: false,
                banned: false,
                teams: { $contains: client.user.teams[0] },
            };

            const query = {
                ...baseQuery,
                ...(search && { name: { $autocomplete: search } }),
                ...(!search && { role: { $eq: 'user' } }),
            };

            try {
                const { users } = await client.queryUsers(query, sort, options);
                // TODO remove undefined check when people service will fix user first, last names bug
                const filteredUsers = users.filter(
                    user =>
                        user.id !== client.userID &&
                        user.name &&
                        user.name !== 'undefined undefined'
                );
                if (filteredUsers.length === 0 && !searchTerm) {
                    instanaCustomEvent({ eventName: 'messages - empty contacts list' });
                }
                return filteredUsers;
            } catch (error) {
                handleError(error);
            }
        },
        [client]
    );

    useEffect(() => {
        (async () => {
            offset.current = 0;
            const users = await fetchUsers(searchTerm);
            setUsers(users);
        })();
    }, [fetchUsers, searchTerm]);

    const loadMore = async () => {
        offset.current += LIMIT;
        const options: SearchOptions = {
            limit: LIMIT,
            offset: offset.current,
        };
        const resUsers = await fetchUsers(searchTerm, options);
        setUsers(users => [...users, ...resUsers]);
    };

    return (
        <ContactList
            eventId={eventId}
            users={users}
            searchTerm={searchTerm}
            loadMore={loadMore}
            onContactItemClick={onContactItemClick}
        />
    );
};

export default ContactListWrapper;
